import { Heading, Text } from '@cinch-labs/design-system'
import { useResponsiveEnvironment } from '@retail-common/util-hooks'
import {
  BaseComponent,
  StoryblokImage,
  storyblokEditable,
} from '@cinch-nx/data-storyblok'

import { SbBlokData } from '@storyblok/react'
import './hero-section.module.css'
import { Wrapper } from '@cinch-nx/shared-ui'
import Image from 'next/image'
import { StoryblokRenderer } from '../../../storyblok-renderer/storyblok-renderer'
import { landingStoryblokComponents } from '../../../storyblok/storyblok-renderer-map'
import styles from './hero-section.module.css'
import cx from 'classnames'

export interface HeroSectionProps extends BaseComponent {
  logo?: StoryblokImage
  heading?: string
  subheading?: string
  ctaButton?: SbBlokData
  image?: StoryblokImage
  classNames?: string[]
}

interface HeroContentProps {
  logo?: StoryblokImage
  heading?: string
  subheading?: string
  ctaButton?: SbBlokData
}

const HeroContent: React.FC<HeroContentProps> = ({
  logo,
  heading,
  subheading,
  ctaButton,
}) => {
  const { isMobile, isTablet } = useResponsiveEnvironment()

  return (
    <div className={styles.brandContent}>
      {logo?.filename && (
        <div className={styles.logoWrapper}>
          <Image
            src={logo.filename}
            alt={logo?.alt ?? 'heroLogo'}
            fill={true}
            style={{
              objectFit: 'contain',
              objectPosition: isMobile || isTablet ? 'center' : 'left center',
            }}
          />
        </div>
      )}

      <Heading element="h1" className={styles.heading}>
        {heading}
      </Heading>
      <Text className={styles.subheading}>{subheading}</Text>
      {ctaButton && (
        <StoryblokRenderer
          block={ctaButton}
          components={landingStoryblokComponents}
        />
      )}
    </div>
  )
}

const DesktopHero: React.FC<HeroSectionProps> = ({
  logo,
  heading,
  subheading,
  ctaButton,
  image,
  classNames,
}: HeroSectionProps) => (
  <div className={cx(styles.heroWrapper, classNames)}>
    <HeroContent
      logo={logo}
      heading={heading}
      subheading={subheading}
      ctaButton={ctaButton}
    />
    {image && (
      <div className={styles.imageWrapper}>
        <Image
          src={image.filename}
          alt={image.alt ?? 'heroImage'}
          className={styles.image}
          quality={100}
          priority
          fill={true}
          style={{
            objectFit: 'contain',
            objectPosition: 'center',
          }}
        />
      </div>
    )}
  </div>
)

const MobileHero: React.FC<HeroSectionProps> = ({
  logo,
  heading,
  subheading,
  ctaButton,
  image,
  classNames,
}: HeroSectionProps) => (
  <div className={cx(classNames)}>
    <HeroContent
      logo={logo}
      heading={heading}
      subheading={subheading}
      ctaButton={ctaButton}
    />
    {image && (
      <div className={styles.imageWrapper}>
        <Image
          src={image.filename}
          alt={image.alt ?? 'heroImage'}
          className={styles.image}
          priority
          quality={100}
          layout="fill"
          objectFit="contain"
        />
      </div>
    )}
  </div>
)

export const HeroSection: React.FC<HeroSectionProps> = (blok) => (
  <Wrapper
    {...storyblokEditable(blok)}
    isFluid
    className={{
      inner: styles.container,
    }}
  >
    <MobileHero {...blok} classNames={[styles.mobileHeroWrapper]} />
    <DesktopHero {...blok} classNames={[styles.desktopHeroWrapper]} />
  </Wrapper>
)
